<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minValue, maxValue, email } from '@vuelidate/validators';
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useConfig } from 'dashboard/composables/useConfig';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import semver from 'semver';
import { getLanguageDirection } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';

export default {
  setup() {
    const { updateUISettings } = useUISettings();
    const { enabledLanguages } = useConfig();
    const { accountId } = useAccount();
    const v$ = useVuelidate();

    return { updateUISettings, v$, enabledLanguages, accountId };
  },
  data() {
    return {
      id: '',
      name: '',
      locale: 'en',
      domain: '',
      supportEmail: '',
      features: {},
      autoResolveDuration: null,
      latestHichatVersion: null,
      showVersionInfo: false,
      _originalForm: null,
    };
  },
  validations: {
    name: {
      required,
    },
    locale: {
      required,
    },
    supportEmail: {
    },
    autoResolveDuration: {
      minValue: minValue(1),
      maxValue: maxValue(999),
    },
  },
  computed: {
    ...mapGetters({
      globalConfig: 'globalConfig/get',
      getAccount: 'accounts/getAccount',
      uiFlags: 'accounts/getUIFlags',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    showAutoResolutionConfig() {
      return this.isFeatureEnabledonAccount(
        this.accountId,
        FEATURE_FLAGS.AUTO_RESOLVE_CONVERSATIONS
      );
    },
    hasAnUpdateAvailable() {
      if (!semver.valid(this.latestHichatVersion)) {
        return false;
      }

      return semver.lt(
        this.globalConfig.appVersion,
        this.latestHichatVersion
      );
    },
    languagesSortedByCode() {
      const enabledLanguages = [...this.enabledLanguages];
      return enabledLanguages.sort((l1, l2) =>
        l1.iso_639_1_code.localeCompare(l2.iso_639_1_code)
      );
    },
    isUpdating() {
      return this.uiFlags.isUpdating;
    },

    featureInboundEmailEnabled() {
      return !!this.features?.inbound_emails;
    },

    featureCustomReplyDomainEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_domain
      );
    },

    featureCustomReplyEmailEnabled() {
      return (
        this.featureInboundEmailEnabled && !!this.features.custom_reply_email
      );
    },

    getAccountId() {
      return this.id.toString();
    },
  },
  mounted() {
    this.initializeAccount();
  },
  methods: {
    async initializeAccount() {
      try {
        const {
          name,
          locale,
          id,
          domain,
          support_email,
          features,
          auto_resolve_duration,
          latest_hichat_version: latestHichatVersion,
        } = this.getAccount(this.accountId);

        this.$root.$i18n.locale = locale;
        this.name = name;
        this.locale = locale;
        this.id = id;
        this.domain = domain;
        this.supportEmail = support_email;
        this.features = features;
        this.autoResolveDuration = auto_resolve_duration;
        this.latestHichatVersion = latestHichatVersion;
        this.snapshotForm();
      } catch (error) {
        // Ignore error
      }
    },

    snapshotForm() {
      this._originalForm = {
        name: this.name,
        locale: this.locale,
        domain: this.domain,
        supportEmail: this.supportEmail,
        autoResolveDuration: this.autoResolveDuration,
      };
    },

    resetForm() {
      if (!this._originalForm) return;
      const { name, locale, domain, supportEmail, autoResolveDuration } = this._originalForm;
      this.name = name;
      this.locale = locale;
      this.domain = domain;
      this.supportEmail = supportEmail;
      this.autoResolveDuration = autoResolveDuration;
      this.v$.$reset();
    },

    async updateAccount() {
      this.v$.$touch();
      if (this.v$.$invalid) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'), { type: 'error' });
        return;
      }
      try {
        await this.$store.dispatch('accounts/update', {
          locale: this.locale,
          name: this.name,
          domain: this.domain,
          support_email: this.supportEmail,
          auto_resolve_duration: this.autoResolveDuration,
        });
        this.$root.$i18n.locale = this.locale;
        this.getAccount(this.id).locale = this.locale;
        this.updateDirectionView(this.locale);
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.SUCCESS'), {type: 'success'});
        this.snapshotForm();
      } catch (error) {
        useAlert(this.$t('GENERAL_SETTINGS.UPDATE.ERROR'), {type: 'error'});
      }
    },

    updateDirectionView(locale) {
      const isRTLSupported = getLanguageDirection(locale);
      this.updateUISettings({
        rtl_view: isRTLSupported,
      });
    },
  },
};
</script>

<template>
  <div class="flex-grow min-w-0">
    <form v-if="!uiFlags.isFetchingItem" @submit.prevent="updateAccount">
      <div class="flex flex-col gap-6">
        <div class="flex flex-col gap-3">
        <div class="flex gap-3">
          <woot-input
            class="flex-1"
            v-model="name"
            :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
            :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
            :error="v$.name.$error ? $t('GENERAL_SETTINGS.FORM.NAME.ERROR') : ''"
            @blur="v$.name.$touch"
          />

          <label :class="{ error: v$.locale.$error }" class="flex-1 mt-1">
            <span class="block text-sm dark:text-n-gray-6 mb-1">
              {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.LABEL') }}
            </span>
            <select v-model="locale">
              <option
                v-for="lang in languagesSortedByCode"
                :key="lang.iso_639_1_code"
                :value="lang.iso_639_1_code"
              >
                {{ lang.name }}
              </option>
            </select>
            <span v-if="v$.locale.$error" class="message">
              {{ $t('GENERAL_SETTINGS.FORM.LANGUAGE.ERROR') }}
            </span>
          </label>
        </div>

          <label v-if="featureCustomReplyDomainEnabled" class="text-xs text-n-gray-5 dark:text-n-gray-6">
            {{
              $t('GENERAL_SETTINGS.FORM.FEATURES.CUSTOM_EMAIL_DOMAIN_ENABLED')
            }}
          </label>
          <label v-if="featureCustomReplyDomainEnabled">
            <woot-input
              v-model="domain"
              :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
              :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
            />
          </label>
          <label v-if="featureCustomReplyEmailEnabled">
            <woot-input
              v-model="supportEmail"
              :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
              :placeholder="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')"
              :error="v$.supportEmail.$error ? $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.ERROR') : ''"
              @blur="v$.supportEmail.$touch"
            />
          </label>
          <div>

            <label class="text-xs dark:text-n-gray-6" v-if="showAutoResolutionConfig"
              :class="{ error: v$.autoResolveDuration.$error }">
              <woot-input
                v-model="autoResolveDuration"
                type="number"
                :label="$t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.LABEL')"
                :placeholder="$t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.PLACEHOLDER')"
                :error="v$.autoResolveDuration.$error ? $t('GENERAL_SETTINGS.FORM.AUTO_RESOLVE_DURATION.ERROR') : ''"
                @blur="v$.autoResolveDuration.$touch"
              />
            </label>
            <label v-if="featureInboundEmailEnabled" class="block text-xs text-n-gray-5 dark:text-n-gray-6 mt-[20px] lg:mt-0">
              {{ $t('GENERAL_SETTINGS.FORM.FEATURES.INBOUND_EMAIL_ENABLED') }}
            </label>
          </div>
        </div>

        <div class="flex flex-col border-slate-25 dark:border-slate-700 text-black-900 dark:text-slate-300">
        <h4 class="text-lg font-medium text-black-900 dark:text-slate-200">
          {{ $t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.TITLE') }}
        </h4>
        <p class="text-xs text-n-gray-6">
          {{ $t('GENERAL_SETTINGS.FORM.ACCOUNT_ID.NOTE') }}
        </p>
        <div class="dark:!bg-n-vars-black-br/30 border border-n-gray-2 dark:border-n-gray-8 rounded-lg">
          <woot-code :script="getAccountId" />
        </div>
      </div>
      <div v-if="showVersionInfo" class="text-sm text-center">
        <div>{{ `v${globalConfig.appVersion}` }}</div>
        <div v-if="hasAnUpdateAvailable && globalConfig.displayManifest">
          {{
            $t('GENERAL_SETTINGS.UPDATE_HICHAT', {
              latestHichatVersion: latestHichatVersion,
            })
          }}
        </div>
        <div class="build-id">
          <div>{{ `Build ${globalConfig.gitSha}` }}</div>
        </div>
      </div>
      <div class="flex items-center justify-center gap-3">
        <button
          type="button"
          class="input-base bg-slate-100 dark:bg-slate-700 !text-slate-700 dark:!text-slate-300 rounded-lg"
          @click="resetForm"
        >
          {{ $t('GENERAL.CANCEL') }}
        </button>
        <woot-submit-button class="!bg-n-bluegray-4 input-base" :button-text="$t('GENERAL_SETTINGS.SUBMIT')"
            :loading="isUpdating" />
      </div>
    </div>
    </form>

    <woot-loading-state v-if="uiFlags.isFetchingItem" />
  </div>
</template>
<style scoped>
input,
select {
  margin: 0 !important;
}
:deep(pre.hljs),
:deep(code.hljs),
:deep(pre),
:deep(code) {
  background: transparent !important;
}
</style>